import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DataTable, Input, useSortableData, type DataTableColumn } from "@shop-ledger/ui-components";
import { usePlatformClient } from "../../api/ClientContext";
import { StatusBanner } from "../../components/StatusBanner";
import { useAsyncData } from "../../lib/useAsyncData";
import type { PlatformTenantSummary } from "../../api/types";

/** A business counts as "active" once it has recorded at least one sale within this
 * window — distinct from `is_suspended` (an admin action), this is purely a signal of
 * whether the business is actually transacting right now. */
const ACTIVE_WINDOW_MS = 48 * 60 * 60 * 1000;

function isRecentlyActive(lastTransactionAt: string | null): boolean {
  if (!lastTransactionAt) return false;
  return Date.now() - new Date(lastTransactionAt).getTime() <= ACTIVE_WINDOW_MS;
}

function formatMoney(decimalString: string, currency: string): string {
  const value = Number(decimalString);
  if (!Number.isFinite(value)) return decimalString;
  return `${currency} ${value.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function formatRelative(iso: string | null): string {
  if (!iso) return "Never transacted";
  const ms = Date.now() - new Date(iso).getTime();
  const minutes = Math.round(ms / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return `${days}d ago`;
}

type ActivityFilter = "all" | "active" | "inactive";
type AccountFilter = "all" | "enabled" | "suspended";
type OnboardingFilter = "all" | "complete" | "incomplete";

type SortKey =
  | "business_name"
  | "business_type"
  | "activity"
  | "turnover_last_30d"
  | "turnover_all_time"
  | "created_at"
  | "is_suspended"
  | "onboarding_complete"
  | "chevron";

/** The Businesses list — every business on the platform, searchable, filterable, and
 * sortable (including by turnover, to see the most active businesses at a glance).
 * Read-only here (suspend/reactivate/backup/impersonate live on the detail page) —
 * clicking anywhere on a row opens it. */
export function TenantsListPage() {
  const client = usePlatformClient();
  const navigate = useNavigate();
  const { data, loading, error } = useAsyncData(() => client.listTenants(), [client]);

  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [activityFilter, setActivityFilter] = useState<ActivityFilter>("all");
  const [accountFilter, setAccountFilter] = useState<AccountFilter>("all");
  const [onboardingFilter, setOnboardingFilter] = useState<OnboardingFilter>("all");

  const businessTypes = useMemo(() => {
    const seen = new Set<string>();
    for (const row of data ?? []) {
      if (row.business_type) seen.add(row.business_type);
    }
    return [...seen].sort();
  }, [data]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return (data ?? []).filter((row) => {
      if (needle && !row.business_name.toLowerCase().includes(needle)) return false;
      if (typeFilter !== "all" && row.business_type !== typeFilter) return false;
      const active = isRecentlyActive(row.last_transaction_at);
      if (activityFilter === "active" && !active) return false;
      if (activityFilter === "inactive" && active) return false;
      if (accountFilter === "enabled" && row.is_suspended) return false;
      if (accountFilter === "suspended" && !row.is_suspended) return false;
      if (onboardingFilter === "complete" && !row.onboarding_complete) return false;
      if (onboardingFilter === "incomplete" && row.onboarding_complete) return false;
      return true;
    });
  }, [data, search, typeFilter, activityFilter, accountFilter, onboardingFilter]);

  const { sortedData, sort, requestSort } = useSortableData<PlatformTenantSummary, SortKey>(
    filtered,
    (row, key) => {
      switch (key) {
        case "business_name":
          return row.business_name.toLowerCase();
        case "business_type":
          return row.business_type ?? "";
        case "activity":
          // Sorts by recency, not just the boolean — most-recently-active first when
          // descending, "never transacted" always sorts last.
          return row.last_transaction_at ? new Date(row.last_transaction_at).getTime() : -Infinity;
        case "turnover_last_30d":
          return Number(row.turnover_last_30d);
        case "turnover_all_time":
          return Number(row.turnover_all_time);
        case "created_at":
          return new Date(row.created_at).getTime();
        case "is_suspended":
          return row.is_suspended ? 1 : 0;
        case "onboarding_complete":
          return row.onboarding_complete ? 1 : 0;
        case "chevron":
          return "";
      }
    },
    { key: "turnover_all_time", direction: "desc" },
  );

  const columns: DataTableColumn<PlatformTenantSummary, SortKey>[] = [
    { key: "business_name", header: "Business", sortable: true, render: (row) => <strong>{row.business_name}</strong> },
    { key: "business_type", header: "Type", sortable: true, render: (row) => row.business_type ?? "—" },
    {
      key: "activity",
      header: "Activity",
      sortable: true,
      render: (row) => {
        const active = isRecentlyActive(row.last_transaction_at);
        return (
          <span
            className={`badge ${active ? "badge--active" : "badge--suspended"}`}
            title={`Last transaction: ${formatRelative(row.last_transaction_at)}`}
          >
            {active ? "Active" : "Inactive"}
          </span>
        );
      },
    },
    {
      key: "turnover_last_30d",
      header: "Turnover (30d)",
      sortable: true,
      align: "right",
      render: (row) => formatMoney(row.turnover_last_30d, row.currency),
    },
    {
      key: "turnover_all_time",
      header: "Turnover (all-time)",
      sortable: true,
      align: "right",
      render: (row) => formatMoney(row.turnover_all_time, row.currency),
    },
    {
      key: "is_suspended",
      header: "Account",
      sortable: true,
      render: (row) => (
        <span className={`badge ${row.is_suspended ? "badge--suspended" : "badge--active"}`}>
          {row.is_suspended ? "Suspended" : "Enabled"}
        </span>
      ),
    },
    {
      key: "onboarding_complete",
      header: "Onboarding",
      sortable: true,
      render: (row) => (
        <span className={`badge ${row.onboarding_complete ? "badge--active" : "badge--suspended"}`}>
          {row.onboarding_complete ? "Onboarded" : "In progress"}
        </span>
      ),
    },
    { key: "created_at", header: "Created", sortable: true, render: (row) => new Date(row.created_at).toLocaleDateString() },
    {
      key: "chevron",
      header: "",
      align: "right",
      render: () => (
        <span className="sl-row-chevron" aria-hidden="true">
          ›
        </span>
      ),
    },
  ];

  return (
    <div className="page">
      <div className="page__header">
        <div>
          <h1 className="page__title">Businesses</h1>
          <p className="page__subtitle">
            Every business on the platform. Click one to view usage, support actions, and impersonate.
          </p>
        </div>
      </div>

      {error ? <StatusBanner kind="error">{error}</StatusBanner> : null}

      <div className="panel filter-bar">
        <div style={{ minWidth: 220, flex: "1 1 220px" }}>
          <Input
            label="Search"
            placeholder="Search by business name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="sl-field">
          <label className="sl-field__label" htmlFor="business-type-filter">
            Type
          </label>
          <select
            id="business-type-filter"
            className="sl-input"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <option value="all">All types</option>
            {businessTypes.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>
        <div className="sl-field">
          <label className="sl-field__label" htmlFor="activity-filter">
            Activity
          </label>
          <select
            id="activity-filter"
            className="sl-input"
            value={activityFilter}
            onChange={(e) => setActivityFilter(e.target.value as ActivityFilter)}
          >
            <option value="all">All</option>
            <option value="active">Active (transacted in last 48h)</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
        <div className="sl-field">
          <label className="sl-field__label" htmlFor="account-filter">
            Account
          </label>
          <select
            id="account-filter"
            className="sl-input"
            value={accountFilter}
            onChange={(e) => setAccountFilter(e.target.value as AccountFilter)}
          >
            <option value="all">All</option>
            <option value="enabled">Enabled</option>
            <option value="suspended">Suspended</option>
          </select>
        </div>
        <div className="sl-field">
          <label className="sl-field__label" htmlFor="onboarding-filter">
            Onboarding
          </label>
          <select
            id="onboarding-filter"
            className="sl-input"
            value={onboardingFilter}
            onChange={(e) => setOnboardingFilter(e.target.value as OnboardingFilter)}
          >
            <option value="all">All</option>
            <option value="complete">Onboarded</option>
            <option value="incomplete">In progress</option>
          </select>
        </div>
      </div>

      <div className="panel">
        <DataTable
          columns={columns}
          rows={sortedData}
          getRowId={(row) => row.id}
          sort={sort}
          onSortChange={requestSort}
          onRowClick={(row) => navigate(`/tenants/${row.id}`)}
          emptyMessage={loading ? "Loading…" : data && data.length > 0 ? "No businesses match these filters." : "No businesses."}
          caption="Businesses"
        />
      </div>
    </div>
  );
}
